<template>
  <Modal
    width="1200"
    :title="selectedTable && `${selectedTable.schema}.${selectedTable.name}`"
    :value="showModal"
    class="table-modal"
    footer-hide
    @on-visible-change="onModalVisibleChange"
  >
    <p
      v-if="selectedTableStack.length > 1"
      slot="header"
      style="height: 100%"
    >
      <Button
        style="margin-right: 16px"
        @click="onBackButtonClick"
      >
        <Icon
          type="md-arrow-back"
          size="24"
        />
      </Button>
      {{ `${selectedTable.schema}.${selectedTable.name}` }}
    </p>
    <div v-if="selectedTable !== null">
      <Tabs
        v-model="selectedTableModalTab"
        :animated="false"
      >
        <TabPane
          label="Table Annotations"
          name="annotation"
        >
          <Table
            max-height="700"
            :columns="tableModalColumns"
            :data="selectedTableViewData"
          />
        </TabPane>
        <TabPane
          label="Outward Table Links"
          name="outward"
        >
          <Table
            max-height="700"
            :columns="outwardForeignKeyColumns"
            :data="outwardForeignKeyData"
            class="link-table"
            @on-row-click="data => onForeignKeyTableRowClick(data)"
          />
        </TabPane>
        <TabPane
          label="Inward Table Links"
          name="inward"
        >
          <Table
            max-height="700"
            :columns="inwardForeignKeyColumns"
            :data="inwardForeignKeyData"
            class="link-table"
            @on-row-click="data => onForeignKeyTableRowClick(data)"
          />
        </TabPane>
      </Tabs>
    </div>
  </Modal>
</template>
<script>
export default {

  model: {
    prop: 'visible',
    event: 'change'
  },

  props: {
    tables: Array,
    table: Object,
    visible: Boolean
  },

  data () {
    return {
      isVisible: this.visible,
      tableArr: this.tables,
      selectedTableStack: [this.table],
      selectedTableModalTab: 'annotation',
      tableModalColumns: [
        {
          title: 'Name',
          key: 'name',
          maxWidth: 200
        },
        {
          title: 'Key',
          key: 'key',
          maxWidth: 200
        },
        {
          title: 'Data Type',
          key: 'type',
          maxWidth: 200
        },
        {
          title: 'Description',
          key: 'description'
        }
      ],
      outwardForeignKeyColumns: [
        {
          title: 'Foreign Key',
          key: 'name'
        },
        {
          title: 'Target Table',
          key: 'targetTable'
        },
        {
          title: 'Target Key',
          key: 'targetKey'
        }
      ],
      inwardForeignKeyColumns: [
        {
          title: 'Pointed Key',
          key: 'sourceKey'
        },
        {
          title: 'Source Table',
          key: 'sourceTable'
        },
        {
          title: 'Foreign Key',
          key: 'name'
        }
      ]
    }
  },

  computed: {
    showModal () {
      return Boolean(this.isVisible && this.selectedTable)
    },

    selectedTable () {
      if (this.selectedTableStack.length === 0) {
        return null
      }

      return this.selectedTableStack.slice(-1)[0]
    },

    outwardForeignKeyData () {
      if (!this.selectedTable) {
        return null
      }

      return this.selectedTable.columns
        .filter(column => column.foreignKey !== null)
        .map(column => {
          const { name, foreignKey } = column
          const { sourceTable, sourceKey } = foreignKey

          return {
            name: name.toUpperCase(),
            targetTable: sourceTable.toUpperCase(),
            targetKey: sourceKey.toUpperCase(),
            direction: 'outward'
          }
        })
    },

    inwardForeignKeyData () {
      if (!this.selectedTable) {
        return null
      }

      const fullTableName = this.getFullTableName(this.selectedTable).toLowerCase()
      const foreignKeyData = []
      this.tableArr.forEach(table => {
        const sourceTableName = this.getFullTableName(table).toUpperCase()
        table.columns.forEach(column => {
          if (column.foreignKey) {
            const targetTable = column.foreignKey.sourceTable.toLowerCase()

            if (fullTableName === targetTable) {
              const { name, foreignKey } = column
              const { sourceKey } = foreignKey

              foreignKeyData.push({
                name: name.toUpperCase(),
                sourceTable: sourceTableName,
                sourceKey: sourceKey.toUpperCase(),
                direction: 'inward'
              })
            }
          }
        })
      })

      return foreignKeyData
    },

    selectedTableViewData () {
      if (!this.selectedTable) {
        return null
      }

      return this.selectedTable.columns.map(column => {
        const { name, type, is_primary_raw: isPrimaryKey, foreignKey, description } = column
        let key = ''

        if (isPrimaryKey) {
          key = 'PRIMARY_KEY'
        } else if (foreignKey) {
          key = 'FOREIGN_KEY'
        }

        return {
          name,
          type,
          key,
          description
        }
      })
    }
  },

  watch: {
    visible (isVisible) {
      this.isVisible = isVisible
    },

    table (newTable) {
      this.selectedTableStack = [newTable]
    },

    tables (newTables) {
      this.tableArr = newTables
    }
  },

  methods: {
    /**
     * Formats table information to get full version of its name: database.schema.table
     * @param {Object} table - table to format full name with
     * @return {String} - full table name
     */
    getFullTableName (table) {
      return `${table.database}.${table.schema}.${table.name}`
    },

    /**
     * Evoked when user closes the table modal
     */
    onTableModalClose () {
      this.selectedTableStack = []
    },

    /**
     * Evoked when a table row on the foreign key section of table modal is clicked
     * @param {Object} data - row data
     */
    onForeignKeyTableRowClick (data) {
      const keyProperty = data.direction === 'outward' ? 'targetTable' : 'sourceTable'
      const selectedTable = this.tableArr.find(table => {
        return this.getFullTableName(table).toLowerCase() === data[keyProperty].toLowerCase()
      })

      if (selectedTable) {
        this.selectedTableStack.push(selectedTable)
        this.selectedTableModalTab = 'annotation'
      }
    },

    /**
     * Evoked when the back button on the table modal is clicked
     */
    onBackButtonClick () {
      this.selectedTableStack.pop()
    },

    /**
     * Evokes when the modal visible status has changed
     * @param {Boolean} visible - true if modal is now visible. False if modal is now not visible
     */
    onModalVisibleChange (visible) {
      this.$emit('change', visible)
    }
  }
}
</script>
<style scoped>
  .table-modal >>> .ivu-modal {
    margin-bottom: 24px;
  }
  .table-modal-header {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    margin-bottom: 16px;
    margin-left: 8px;
  }
  .link-table >>> .ivu-table-row {
    cursor: pointer;
  }
</style>
